import type { BuilderBrochureSettings } from "api-types/modelTypes";
import type { DrawingTemplateInstanceNewOptions } from "building-generation/contentTemplates/contentTemplateInstanceWrapper";
import { drawingTemplateInstanceNew } from "building-generation/contentTemplates/contentTemplateInstanceWrapper";
import { doorIntersectsCutPlane } from "building-generation/elements/doors/doorQuery";
import { windowIntersectsCutPlane } from "building-generation/elements/window/windowQuery";
import { formatRoomArea, formatRoomDims, formatRoomLabelDimText } from "building-generation/utils/floorplan";
import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ParameterValueHandle } from "building-types/options/parameter";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { RoomParameterValues } from "building-types/systemParameterTypes";
import type { DrawingTemplateInstance, PlanViewTemplate } from "building-types/views/planSets";
import type { RoomTagCapitalization } from "building-types/views/planSets";
import { DEFAULT_ROOM_TAG_CAPITALIZATION } from "building-types/views/planSets";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { WallType } from "building-types/walls/wallEnums";
import { Lazy } from "common/data/lazy";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { wallIndexForLevel } from "doc/buildingHandleUtils";
import { isTagDrawingTemplateId } from "doc/docGetContentTemplates";
import { getRoomGarageCount } from "doc/getGarageCount";
import { evaluateParameterDefaultValues } from "doc/parameterCoreTools";
import { LazyPrimitiveAdapter, parameterValuesMerge } from "doc/parameterTools";
import { DIM_LW } from "doc/plans/constants";
import type { PlanCollider, VisitedRoomsTable } from "doc/plans/interiorLabelObstacle";
import { CollisionType, isValidRoomSet } from "doc/plans/interiorLabelObstacle";
import type { PlanGenerationContext, SectionGenerationContext } from "doc/plans/planGeometrySource";
import type { TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { textSizeInView } from "doc/plans/primitives/textPrimitive";
import { makePlanColliderFromSheetGeometry } from "doc/plans/sheetGeometryCollision";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { applyRoomTagCapitalization, roomTypeParameterValue } from "doc/rooms/roomTagParameterValues";
import { getRoomLabelAttributes } from "doc/settingsTools";
import { roomParameters } from "doc/systemParameters";
import { systemParametersFromDocWithoutBuilding } from "doc/systemParameterTools";
import {
  aabb2AddPoint,
  aabb2ByCenterWidthHeight,
  aabb2Center,
  aabb2Copy,
  aabb2Grow,
  aabb2IsValid,
  aabb2Union,
} from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { polyLine2AABB2, polyLine2Centroid, polyLine2FromAABB2, polyLine2Rotate } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Label } from "lib/house/plans/label";
import { labelNew } from "lib/house/plans/label";
import type { RoomLabelFormatter } from "lib/house/plans/planRoomLabelFormatting";
import {
  ceilingHeightWithOverride,
  floorMaterialWithOverride,
  formatRoomLabelDefault,
  formatRoomLabelSanitized,
} from "lib/house/plans/planRoomLabelFormatting";
import type { RoomDimensions } from "lib/house/plans/roomDecompositionForLabeling";
import {
  computeInaccessibilityPolygonRoomDimensions,
  makeRoomNameAndDimLabelsByRoomDecomposition,
} from "lib/house/plans/roomDecompositionForLabeling";
import { ROOM_LABEL_FONT_SIZE, ROOM_LABEL_FONT_SIZE_DECOMP } from "pdf/planFonts";
import { makeSheetGeometryFromTagTemplateInstances } from "../../../studio/planSetTemplateToPages";
import { buildOrthogonalSectionLabels } from "../orthogonalSections";
import { getReportingUnitRoomIds } from "../reportingUnitRooms";
import { makeSheetGeometryFromLabels } from "./planLabels";

export function makeSheetGeometryForChaseRooms({ building, levelId, cutPlaneHeight }: PlanGenerationContext) {
  const result: SheetGeometry[] = [];

  const levelRooms = roomsOnLevel(levelId, building.rooms, building.roomsByLevel);
  const roomOutlines = building.roomOutlineTable;

  // Some rooms (like chase rooms) have no doors. These room outlines need to be
  // subtracted from the level wall polygon. We do this by rendering them after the primary
  // wall cross sections in white.
  for (const room of levelRooms) {
    const wallIndex = wallIndexForLevel(building, levelId);
    const roomWalls = wallIndex.wallsInRoom(room);

    const roomHasDoorOrWindow =
      roomWalls.findIndex(
        (x) =>
          x.wallType === WallType.VOID ||
          (x.wallType === WallType.SOLID &&
            (x.doorPlacements.filter((doorPlacement) =>
              doorIntersectsCutPlane(doorPlacement, cutPlaneHeight, building.roomFloorAssemblies),
            ).length > 0 ||
              x.windowPlacements.filter((windowPlacement) =>
                windowIntersectsCutPlane(windowPlacement, cutPlaneHeight, building.roomFloorAssemblies),
              ).length > 0)),
      ) >= 0;

    if (roomHasDoorOrWindow) {
      continue;
    }

    const roomOutline = roomOutlines[room.id].INSIDE_OF_MASS_IGNORE_VOID;
    assertIsNotUndefined(roomOutline);

    result.push(
      sheetPolylineGeometryNew({
        handle: room.roomHandle,
        paths: [roomOutline],
      }),
    );
  }

  return result;
}

export enum RoomLabelPositioningMethod {
  InaccessibiltyPolygon,
  RoomDecomposition,
}

interface PlanRoomLabelProps
  extends Pick<
      PlanGenerationContext,
      "building" | "levelId" | "noSettings" | "viewTemplateId" | "planRotationRads" | "selectedReportingUnitLabelId"
    >,
    Partial<Pick<BuilderBrochureSettings, "displayPlanLabelDims">> {
  positioning?: RoomLabelPositioningMethod;
}

interface InteriorLabels {
  colliders: PlanCollider[][];
  drawingTemplateInstances: DrawingTemplateInstance[];
}

export function makeSheetGeometryFromRoomLabels(props: PlanRoomLabelProps) {
  const { building, levelId, noSettings, planRotationRads } = props;

  const { colliders, drawingTemplateInstances } = makeInteriorLabelsFromRoomLabels(props);

  const labels = extractRoomLabels(colliders);

  const labelSheetGeometry = makeSheetGeometryFromLabels({
    handle: building.levels[levelId].handle,
    labels,
    noSettings,
  });

  const sheetGeometryFromTagTemplate = makeSheetGeometryFromTagTemplateInstances({
    doc: building.doc,
    building,
    tagTemplateInstances: drawingTemplateInstances,
    planRotationRads,
    preserveOrientation: true,
  });

  return labelSheetGeometry.concat(sheetGeometryFromTagTemplate);
}

/**
 * Room labels on the cut plane of a section view. The labels are positioned by intersecting each
 * room's volume with the section's front plane, so they only exist for rooms the section passes
 * through.
 */
export function makeSheetGeometryFromSectionRoomLabels({
  building,
  sectionViewId,
}: SectionGenerationContext): SheetGeometry[] {
  const sectionView = building.doc.sectionViews[sectionViewId];
  if (!sectionView || !building.groundLevel) {
    return [];
  }

  return makeSheetGeometryFromLabels({
    handle: building.groundLevel.handle,
    labels: buildOrthogonalSectionLabels({ sectionView, building }),
  });
}

function makeInteriorLabelsFromRoomLabels({
  levelId,
  positioning = RoomLabelPositioningMethod.InaccessibiltyPolygon,
  building,
  displayPlanLabelDims = true,
  viewTemplateId,
  selectedReportingUnitLabelId,
}: PlanRoomLabelProps): InteriorLabels {
  const planViewTemplate =
    viewTemplateId &&
    building.doc.viewTemplates[viewTemplateId] &&
    building.doc.viewTemplates[viewTemplateId].viewType === "PLAN_VIEW"
      ? building.doc.viewTemplates[viewTemplateId]
      : undefined;

  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  switch (positioning) {
    case RoomLabelPositioningMethod.InaccessibiltyPolygon: {
      return makeRoomLabelsByInaccessibilityPolygon(
        building,
        levelId,
        formatRoomLabelDefault,
        planViewTemplate,
        reportingUnitRoomIds,
      );
    }
    case RoomLabelPositioningMethod.RoomDecomposition: {
      return makeRoomLabelsByRoomDecomposition({
        building,
        levelId,
        formatRoomNameLabel: formatRoomLabelSanitized,
        displayPlanLabelDims,
        planViewTemplate,
        reportingUnitRoomIds,
      });
    }
  }
}

function addInteriorLabel(
  interiorLabels: PlanCollider[][],
  collisionType: CollisionType,
  rangeAABB: AABB2,
  label: string,
  fontSize: SheetInches,
  x: number,
  y: number,
  labelBorder: number,
  labelRotation: number,
  labelRotationIsSheet?: boolean,
  dimensionLine?: PolyLine2,
  baseLine?: PolyLine2,
  appendToPrevGroup: boolean = false,
  mass?: number,
  moveOnDimLine: boolean = false,
  handle?: DocEntityHandle,
) {
  const [w, h] = textSizeInView(label, fontSize);
  const baseAABB = aabb2ByCenterWidthHeight(x, y, w, h);
  aabb2Grow(baseAABB, labelBorder, labelBorder);

  if (dimensionLine) {
    aabb2AddPoint(baseAABB, baseAABB.minX, baseAABB.minY - DIM_LW);
  }

  const labelPerimeter = polyLine2Rotate(polyLine2FromAABB2(baseAABB), labelRotation, x, y - DIM_LW);
  const labelAABB = polyLine2AABB2(labelPerimeter);

  let fullAABB = aabb2Copy(labelAABB);
  if (dimensionLine) {
    fullAABB = aabb2Union(fullAABB, polyLine2AABB2(dimensionLine));
  }

  const toAdd: PlanCollider = {
    collisionType,
    rangeAABB: aabb2Union(rangeAABB, labelAABB),
    labelPerimeter,
    labelAABB,
    fullAABB,
    label,
    labelHeight: h,
    baseLine,
    dimensionLine,
    labelRotation,
    labelRotationBasis: labelRotationIsSheet ? "SHEET" : "PLAN",
    fontSize,
    mass,
    moveOnDimLine,
    handle,
  };

  if (!appendToPrevGroup || interiorLabels.length === 0) {
    interiorLabels.push([toAdd]);
  } else {
    interiorLabels[interiorLabels.length - 1].push(toAdd);
  }
}

const EMPTY_ROOM_PARAMETER_VALUES: RoomParameterValues = {
  roomReportedName: "",
  roomArea: "",
  roomCeilingHeight: "",
  roomDimensions: "",
  roomFloorMaterial: "",
  roomTagDetails: "",
  roomType: "",
  roomBathroomCount: "",
  roomBedroomCount: "",
  roomGarageCount: "",
};

function addDrawingTemplateInstance(
  drawingTemplateInstances: DrawingTemplateInstance[],
  drawingTemplateOptions: Omit<DrawingTemplateInstanceNewOptions, "parameterValues">,
  room: Room,
  building: Building,
  dimensions: Lazy<RoomDimensions>,
  roomTagCapitalization: RoomTagCapitalization = DEFAULT_ROOM_TAG_CAPITALIZATION,
) {
  const roomParameterDefaults = { ...EMPTY_ROOM_PARAMETER_VALUES };

  const { roomLabelUnits, roomLabelRounding } = getRoomLabelAttributes(building);
  const { systemParameterDefaultValues } = systemParametersFromDocWithoutBuilding(building.doc, building.optionChoices);
  parameterValuesMerge(
    roomParameterDefaults,
    evaluateParameterDefaultValues(roomParameters(systemParameterDefaultValues)),
  );
  parameterValuesMerge(
    roomParameterDefaults,
    evaluateParameterDefaultValues(
      roomParameters({
        roomCeilingHeight: ceilingHeightWithOverride(building.roomFloorAssemblies, room),
        roomFloorMaterial: floorMaterialWithOverride(building.floorSystems, room),
        roomTagDetails: room.tagDetails,
        roomReportedName: room.nameInPlan || room.attributes.name,
        roomType: roomTypeParameterValue(room.type),
        roomBathroomCount: String(room.attributes.bathroomCount ?? 0),
        roomGarageCount: String(getRoomGarageCount(building, room)),
        roomBedroomCount: String(room.attributes.bedroomCount ?? 0),
      }),
    ),
  );

  const {
    roomCeilingHeight: rawRoomCeilingHeight,
    roomFloorMaterial: rawRoomFloorMaterial,
    roomReportedName: rawRoomReportedName,
    roomTagDetails: rawRoomTagDetails,
    roomType: rawRoomType,
    roomBathroomCount,
    roomBedroomCount,
    roomGarageCount,
  } = roomParameterDefaults;

  // BathroomCount, BedroomCount, GarageCount, Area, and Dimensions have fixed capitalization.
  const roomCeilingHeight = applyRoomTagCapitalization(rawRoomCeilingHeight, roomTagCapitalization);
  const roomFloorMaterial = applyRoomTagCapitalization(rawRoomFloorMaterial, roomTagCapitalization);
  const roomReportedName = applyRoomTagCapitalization(rawRoomReportedName, roomTagCapitalization);
  const roomTagDetails = applyRoomTagCapitalization(rawRoomTagDetails, roomTagCapitalization);
  const roomType = applyRoomTagCapitalization(rawRoomType, roomTagCapitalization);

  const roomDimensions = new LazyPrimitiveAdapter(
    dimensions.map(({ width, height }) => {
      if (room.attributes.labelHeightOverride && room.attributes.labelWidthOverride) {
        return `${room.attributes.labelWidthOverride} x ${room.attributes.labelHeightOverride}`;
      }

      return formatRoomDims(width, height, roomLabelUnits, roomLabelRounding);
    }),
  );
  const roomArea = new LazyPrimitiveAdapter(
    dimensions.map(({ area }) => {
      if (room.areaTagOverride) {
        return room.areaTagOverride;
      }

      return formatRoomArea(area, roomLabelRounding);
    }),
  );

  const parameterValueHandles: Record<keyof RoomParameterValues, ParameterValueHandle> = {
    roomCeilingHeight: { type: "PRIMITIVE", value: roomCeilingHeight },
    roomArea: roomArea,
    roomDimensions: roomDimensions,
    roomFloorMaterial: { type: "PRIMITIVE", value: roomFloorMaterial },
    roomReportedName: { type: "PRIMITIVE", value: roomReportedName },
    roomTagDetails: { type: "PRIMITIVE", value: roomTagDetails },
    roomType: { type: "PRIMITIVE", value: roomType },
    roomBathroomCount: { type: "PRIMITIVE", value: roomBathroomCount },
    roomBedroomCount: { type: "PRIMITIVE", value: roomBedroomCount },
    roomGarageCount: { type: "PRIMITIVE", value: roomGarageCount },
  };

  drawingTemplateInstances.push(
    drawingTemplateInstanceNew({ ...drawingTemplateOptions, parameterValues: parameterValueHandles }),
  );
}

function makeRoomLabelsByInaccessibilityPolygon(
  building: Building,
  levelId: string,
  formatRoomLabel: RoomLabelFormatter = formatRoomLabelDefault,
  planViewTemplate?: PlanViewTemplate,
  reportingUnitRoomIds?: ReadonlySet<string>,
): InteriorLabels {
  const interiorLabels: PlanCollider[][] = [];
  const drawingTemplateInstances: DrawingTemplateInstance[] = [];

  const fontSize = ROOM_LABEL_FONT_SIZE;
  const roomTagTemplateId = planViewTemplate?.roomTagTemplateId;

  // Configurable capitalization is only applied to the newer template-based system.
  const roomTagCapitalization = planViewTemplate?.roomTagCapitalization ?? DEFAULT_ROOM_TAG_CAPITALIZATION;

  const roomFilter = reportingUnitRoomIds ? (room: Room) => reportingUnitRoomIds.has(room.id) : undefined;

  roomTagsPlaceByInaccessibilityPolygon(
    building,
    levelId,
    formatRoomLabel,
    (room, aabb, label, cx, cy) => {
      const resolvedRoomTagTemplateId = room.roomTagTemplateId ?? roomTagTemplateId;
      if (isTagDrawingTemplateId({ contentTemplates: building.doc.contentTemplates }, resolvedRoomTagTemplateId)) {
        const lazyDimensions = new Lazy(() =>
          computeInaccessibilityPolygonRoomDimensions(room, building.rooms, building, levelId),
        );

        addDrawingTemplateInstance(
          drawingTemplateInstances,
          {
            contentTemplateId: resolvedRoomTagTemplateId,
            parentId: room.id,
            x: cx,
            y: cy,
          },
          room,
          building,
          lazyDimensions,
          roomTagCapitalization,
        );
      } else {
        addInteriorLabel(
          interiorLabels,
          CollisionType.RoomName,
          aabb,
          label.toUpperCase(),
          fontSize,
          cx,
          cy,
          2,
          0,
          true, // room labels are oriented to sheet space
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          room.roomHandle,
        );
      }
    },
    roomFilter,
  );
  return { colliders: interiorLabels, drawingTemplateInstances };
}

export function roomTagsPlaceByInaccessibilityPolygon(
  building: Building,
  levelId: string,
  formatRoomLabel: RoomLabelFormatter = formatRoomLabelDefault,
  callback: (room: Room, aabb: AABB2, label: string, cx: number, cy: number) => void,
  roomFilter?: (room: Room) => boolean,
) {
  const { rooms } = building;
  const wallConfig = building.settings.wall;
  if (!building.wallMasses[levelId]) {
    // On the editor viewer, the building has levels without wall masses.
    return;
  }
  const roomSets = [...building.wallMasses[levelId].openConnectedRoomSets];

  for (const room of roomsOnLevel(levelId, building.rooms, building.roomsByLevel).filter(
    (x) => x.type === RoomType.PATIO,
  )) {
    roomSets.push([room]);
  }

  const visitedRooms: VisitedRoomsTable = {};
  for (const roomSet of roomSets) {
    // validate room set
    if (!isValidRoomSet(roomSet, rooms, visitedRooms)) {
      continue;
    }

    // calculate the aabb of the room set
    let roomSetAABB = aabb2New(
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    );

    for (const roomLike of roomSet) {
      const { id } = roomLike;
      const { outline } = rooms[id];

      roomSetAABB = aabb2Union(
        roomSetAABB,
        aabb2Grow(polyLine2AABB2(outline), -wallConfig.interiorWallOffset, -wallConfig.interiorWallOffset),
      );
    }

    if (!aabb2IsValid(roomSetAABB)) {
      continue;
    }

    // add room names
    for (const roomLike of roomSet) {
      const { id, attributes } = roomLike;
      const { outline } = rooms[id];

      if (attributes?.roomTagHiddenInPlan) {
        continue;
      }

      if (roomFilter && !roomFilter(roomLike)) {
        continue;
      }

      const poleOrNull = poleOfInaccessibilityPolyLine2(outline);
      assertIsNotNull(poleOrNull);
      const [cx, cy] = poleOrNull;
      const label = formatRoomLabel(
        building.settings.planset,
        roomLike,
        building.roomFloorAssemblies,
        building.floorSystems,
      );

      callback(roomLike, roomSetAABB, label, cx, cy);
    }
  }
}

interface MakeRoomLabelsByRoomDecompositionParams extends Pick<BuilderBrochureSettings, "displayPlanLabelDims"> {
  building: Building;
  levelId: string;
  formatRoomNameLabel?: RoomLabelFormatter;
  planViewTemplate?: PlanViewTemplate;
  reportingUnitRoomIds?: ReadonlySet<string>;
}

function makeRoomLabelsByRoomDecomposition({
  building,
  levelId,
  formatRoomNameLabel = formatRoomLabelDefault,
  displayPlanLabelDims,
  planViewTemplate,
  reportingUnitRoomIds,
}: MakeRoomLabelsByRoomDecompositionParams) {
  const { roomLabelRounding, roomLabelUnits } = getRoomLabelAttributes(building);
  const { rooms, roomsByLevel } = building;
  const planarGraph = building.planarGraphs[levelId].planarGraph;
  const polygonPlanarGraph = building.planarGraphs[levelId];
  const wallMassSystem = building.wallMasses[levelId];
  const roomLevelIndices = building.roomLevelIndices;
  const levelRoomIds = roomsByLevel[levelId];

  const roomSets = [...building.wallMasses[levelId].openConnectedRoomSets];

  for (const room of roomsOnLevel(levelId, building.rooms, building.roomsByLevel).filter(
    (x) => x.type === RoomType.PATIO,
  )) {
    roomSets.push([room]);
  }

  const interiorLabels: PlanCollider[][] = [];
  const drawingTemplateInstances: DrawingTemplateInstance[] = [];

  const roomTagTemplateId = planViewTemplate?.roomTagTemplateId;
  // Configurable capitalization is only applied to the newer template-based system.
  const roomTagCapitalization = planViewTemplate?.roomTagCapitalization ?? DEFAULT_ROOM_TAG_CAPITALIZATION;

  const { roomDecomps: roomNameAndDimLabelArr } = makeRoomNameAndDimLabelsByRoomDecomposition({
    roomSets,
    rooms,
    levelRoomIds,
    polygonPlanarGraph,
    roomLevelIndices,
    planarGraph,
    wallMassSystem,
    formatRoomNameLabel,
    // Could pass `building.settings.planset` but deliberately not doing so for brochures for now.
    floorPlanSettings: { addCeilingHeightToRoomTag: false, addFloorMaterialToRoomTag: false },
    roomFloorAssemblies: building.roomFloorAssemblies,
    floorSystems: building.floorSystems,
  });

  for (const {
    presentationalHeight,
    presentationalWidth,
    name: roomName,
    hideDims,
    pl,
    widthOverride,
    heightOverride,
    roomId,
    area,
  } of roomNameAndDimLabelArr) {
    // Restrict room tags to the selected reporting unit. A decomposed label without a resolvable
    // roomId can't be attributed to a unit, so drop it while a unit filter is active.
    if (reportingUnitRoomIds && (!roomId || !reportingUnitRoomIds.has(roomId))) {
      continue;
    }

    const centroidOrNull = polyLine2Centroid(pl);
    assertIsNotNull(centroidOrNull);
    const [cx, cy] = centroidOrNull;

    const representativeRoom = roomId ? rooms[roomId] : null;
    const resolvedRoomTagTemplateId = representativeRoom?.roomTagTemplateId ?? roomTagTemplateId;

    if (
      isTagDrawingTemplateId({ contentTemplates: building.doc.contentTemplates }, resolvedRoomTagTemplateId) &&
      roomId
    ) {
      const dimensions = new Lazy(() => ({ width: presentationalWidth, height: presentationalHeight, area }));
      addDrawingTemplateInstance(
        drawingTemplateInstances,
        {
          contentTemplateId: resolvedRoomTagTemplateId,
          parentId: roomId,
          x: cx,
          y: cy,
        },
        rooms[roomId],
        building,
        dimensions,
        roomTagCapitalization,
      );
    } else {
      const labelText = formatRoomLabelDimText({
        roomName,
        width: presentationalWidth,
        height: presentationalHeight,
        hideDims: hideDims || !displayPlanLabelDims,
        widthOverride,
        heightOverride,
        roomLabelUnits,
        roomLabelRounding,
      });

      const roomSetAABB = polyLine2AABB2(pl);
      addInteriorLabel(
        interiorLabels,
        CollisionType.RoomName,
        roomSetAABB,
        labelText,
        ROOM_LABEL_FONT_SIZE_DECOMP,
        cx,
        cy,
        2,
        0,
      );
    }
  }

  return { colliders: interiorLabels, drawingTemplateInstances };
}

function extractRoomLabels(interiorLabels: PlanCollider[][]) {
  const labels: Label[] = [];

  // draw labels
  for (let i = 0, l = interiorLabels.length; i < l; i++) {
    for (let j = 0, jc = interiorLabels[i].length; j < jc; j++) {
      const { dimensionLine, labelAABB, label, labelRotation, labelRotationBasis, fontSize, mass, rangeAABB, handle } =
        interiorLabels[i][j];

      let x: number | undefined;
      let y: number | undefined;

      if (mass === undefined || mass >= 0) {
        [x, y] = aabb2Center(labelAABB);
      }

      if (dimensionLine) {
      } else {
        assertIsNotUndefined(fontSize);
        labels.push(
          labelNew({
            text: label,
            fontSize,
            centerX: x,
            centerY: y,
            textRotationRads: labelRotation,
            textRotationBasis: labelRotationBasis,
            relaxationRangeAABB: rangeAABB,
            handle,
          }),
        );
      }
    }
  }

  return labels;
}

export function roomPlanColliders(labels: readonly TaggedPlanGeometry[]): (PlanCollider | undefined)[] {
  return labels.map((rl) => {
    if (rl.type === SheetGeometryType.TEXT) {
      return makePlanColliderFromSheetGeometry({
        collisionType: CollisionType.RoomName,
        sheetTextGeomSource: rl,
        labelBorder: 2,
      });
    }
    return undefined;
  });
}
